import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { setSelectedMenuItem } from "@/components/Menu/menuSlice";
import { useAccount } from '@/hooks/useAccount';
import { getAccountItemImages, saveAccountItem } from '@/lib/account-data';
import { blobToBase64 } from "@/lib/blob-utils";
import type { SaveItemImage } from '@/types';
import { ActionIcon, Box, Button, Divider, Drawer, FileInput, Flex, Grid, Group, Image, ScrollArea, SimpleGrid, Stack, Text, Textarea, TextInput, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from '@mantine/notifications';
import { IconCamera, IconChevronLeft, IconCircleMinus, IconMinus, IconPlus } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import { saveItem, saveMode, setSaveItem, setSaveMode } from "./saveSlice";

export const SavePage: React.FC = () => {
    const dispatch = useAppDispatch();
    const { userId } = useAccount();
    const currentSaveItem = useAppSelector(saveItem);
    const currentSaveMode = useAppSelector(saveMode);
    const fileUploadRef = useRef<HTMLButtonElement>(null);
    const [opened, { close, open }] = useDisclosure(false);
    const [itemName, setItemName] = useState<string>('');
    const [itemNameError, setItemNameError] = useState<string>('');
    const [notes, setNotes] = useState<string>('');
    const [newTag, setNewTag] = useState<string>('');
    const [tags, setTags] = useState<string[]>([]);
    const [images, setImages] = useState<Pick<SaveItemImage, 'imgSrc'>[]>([]);
    const [imagesLoading, setImagesLoading] = useState(false);
    const [imageError, setImageError] = useState('');
    const [saving, setSaving] = useState(false);
    const savingRef = useRef(false);
    const selectionRef = useRef(0);
    const [tagError, setTagError] = useState<string>('');

    useEffect(() => {
        const selection = ++selectionRef.current;
        let cancelled = false;
        if (currentSaveItem) {
            const loadSaveItem = async () => {
                setImageError('');
                setItemNameError('');
                setNewTag('');
                setImagesLoading(true);
                setItemName(currentSaveItem.name ?? '');
                setNotes(currentSaveItem.notes ?? '');
                setTags(currentSaveItem.tags);
                setImages([]);
                open();
                try {
                    if (currentSaveItem.id !== null) {
                        const saveImages = await getAccountItemImages(userId, currentSaveItem.id);
                        if (!cancelled && selectionRef.current === selection) setImages(saveImages.filter((image) => !!image.imgSrc));
                    }
                } catch {
                    if (!cancelled) setImageError('Photos could not be loaded. Close and reopen this item to retry.');
                } finally {
                    if (!cancelled) setImagesLoading(false);
                }
            }
            void loadSaveItem();
        }
        return () => { cancelled = true; selectionRef.current = selection + 1; };
    }, [currentSaveItem, userId, open])

    useEffect(() => {
        if(opened){
            dispatch(setSelectedMenuItem('closed'));
        }
    },[opened, dispatch])

    const removeImageHandler = (index: number) => {
        setImages((current) => current.filter((_, i) => i !== index));
    }

    const closeSavePage = () => {
        if (savingRef.current) return;
        dispatch(setSaveItem(null));
        close();
    };

    const addImageHandler = async (files: File[]) => {
        const selection = selectionRef.current;
        setImagesLoading(true);
        try {
            const newImages = await Promise.all(files.map(async (file) => ({ imgSrc: await blobToBase64(file) })));
            if (selectionRef.current === selection) setImages((current) => [...current, ...newImages]);
        } catch {
            notifications.show({ color: 'red', message: 'A photo could not be read. Please try adding it again.' });
        } finally {
            if (selectionRef.current === selection) setImagesLoading(false);
        }
    }

    const newTagChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTagError('');
        setNewTag(event.currentTarget.value);
    }

    const itemNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setItemNameError('');
        setItemName(event.currentTarget.value);
    }

    const addTagHandler = () => {
        setTagError('');
        if (newTag.trim() === '') {
            setTagError('Tag cannot be empty');
            return;
        }
        if (tags.includes(newTag.trim())) {
            setTagError('Tag already exists');
            return;
        }
        setNewTag('');
        setTags([...tags, newTag]);
    }

    const onSaveClickHandler = async () => {
        if (savingRef.current || imagesLoading || imageError) return;
        if (itemName.trim() === '') {
            setItemNameError('Item Name cannot be empty');
            return;
        }
        if (currentSaveItem) {
            const selection = selectionRef.current;
            savingRef.current = true;
            setSaving(true);
            try {
                const newSaveItem = {
                    name: itemName,
                    notes: notes,
                    tags: tags,
                    itemType: currentSaveItem.itemType ?? 'point',
                    shape: currentSaveItem.shape,
                    color: currentSaveItem.color ?? 'blue',
                    size: currentSaveItem.size ?? 'small',
                    hasImages: images.length > 0,
                    itemDetails: currentSaveItem.itemDetails ?? [],
                    visible: currentSaveItem.visible ?? true,
                    createdAt: currentSaveItem.createdAt ?? new Date().getTime(),
                }
                await saveAccountItem(userId, currentSaveItem.id, newSaveItem, images);
                // A sign-out/account switch can unmount this drawer while storage commits.
                if (selectionRef.current !== selection) return;
                notifications.show({
                    title: 'Item Saved',
                    message: `${newSaveItem.name} has been saved successfully`,
                    color: 'green',
                    icon: <IconPlus />
                })
                dispatch(setSaveItem(null));
                close();
            } catch {
                if (selectionRef.current === selection) notifications.show({ title: 'Unable to save', message: 'Your changes are still here. Check available storage and try again.', color: 'red' });
            } finally {
                savingRef.current = false;
                if (selectionRef.current === selection) setSaving(false);
            }
        }
    }

    return (
        <Drawer
            position="left"
            withCloseButton={false}
            styles={{ body: { height: '100%' } }}
            size='100%'
            opened={opened}
            onClose={closeSavePage}

        >
            <Stack style={{ height: '100%' }} gap={5}>
                <Group align='center' justify='center' style={{ padding: '0' }}>
                    <ActionIcon disabled={saving} style={{ position: 'absolute', top: 15, left: 20 }} onClick={closeSavePage} variant='transparent' size='md' aria-label='Close Save Item'>
                        <IconChevronLeft color='black' />
                    </ActionIcon>
                    <Title order={4} mb={10}>{`${currentSaveMode === 'edit' ? 'Edit' : currentSaveMode === 'new' ? 'Add' : 'View'} Item`}</Title>
                    {currentSaveMode === 'view' ?
                        <Button style={{ position: 'absolute', top: 10, right: 10 }} onClick={() => dispatch(setSaveMode('edit'))} variant='transparent' size='md' color="pq-red" aria-label='Edit Item'>
                            Edit
                        </Button> :
                        <Button loading={saving} disabled={imagesLoading || !!imageError} style={{ position: 'absolute', top: 10, right: 10 }} onClick={onSaveClickHandler} variant='transparent' size='md' color="pq-red" aria-label='Save Item'>
                            Save
                        </Button>
                    }
                </Group>
                <Divider my={0} />
                <ScrollArea h='100%' py={5} type='never'>
                    <Stack gap='sm' mx="md">
                        {imageError && <Text role="alert" c="red">{imageError}</Text>}
                        <TextInput data-autofocus readOnly={saving || currentSaveMode === 'view'} error={itemNameError} placeholder="e.g Cool Parcel" label='Item Name' value={itemName} onChange={itemNameChangeHandler}></TextInput>
                        <Stack gap={2}>
                            {
                                currentSaveItem?.itemDetails?.map((itemDetail, index) => {
                                    return (
                                        <Text key={index} size='sm'>{itemDetail}</Text>
                                    )
                                })
                            }
                        </Stack>
                        <Textarea readOnly={saving || currentSaveMode === 'view'} minRows={3} autosize placeholder="e.g Some really cool details about this item" label='Notes' value={notes} onChange={(event) => setNotes(event.currentTarget.value)}></Textarea>
                        {currentSaveMode !== 'view' && <TextInput placeholder="e.g Fire Potential" error={tagError} label='Tags' value={newTag} onChange={newTagChangeHandler} rightSection={
                            <ActionIcon disabled={saving} onClick={addTagHandler} variant='transparent' color="red" size='sm' radius='xl' aria-label='Add Tag'>
                                <IconPlus color='#B31E3B' />
                            </ActionIcon>
                        }></TextInput>
                        }
                        <SimpleGrid cols={2}>

                            {
                                tags.map((tag, index) => {
                                    return (
                                        <Button disabled={saving || currentSaveMode === 'view'} key={index} onClick={() => setTags(tags.filter((_, i) => i !== index))} color='pq-med-gray' style={{ color: 'black' }} leftSection={currentSaveMode !== 'view' ? <Box h={20}><IconCircleMinus color='#B31E3B' /></Box> : undefined}>{tag}</Button>
                                    )
                                })
                            }
                        </SimpleGrid>
                        {currentSaveMode !== 'view' && <Flex justify='center' align='center'><Button disabled={saving || imagesLoading || !!imageError} color='pq-red' onClick={() => fileUploadRef.current?.click()} rightSection={<IconCamera />}>Add</Button></Flex>}
                        {currentSaveMode !== 'view' && <FileInput ref={fileUploadRef} style={{ display: 'none' }} multiple accept='image/*' onChange={(files) => addImageHandler(files)} />}
                        <Grid gutter='md'>
                            {
                                images.map((image, index) => {
                                    return (
                                        image.imgSrc &&
                                        <Grid.Col key={index} span={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                                            <Box style={{ position: 'relative', boxShadow: 'rgba(0, 0, 0, 0.35) 0px 5px 15px'}}>
                                                <Image fit='contain' src={image.imgSrc} alt={`Image ${index}`} />
                                                {currentSaveMode !== 'view' && <ActionIcon disabled={saving || imagesLoading} style={{ position: 'absolute', top: 10, right: 10 }} onClick={() => removeImageHandler(index)} size="md" radius="xl" color="pq-red" aria-label='Remove Image'>
                                                    <IconMinus stroke={3} color='black' />
                                                </ActionIcon>
                                                }

                                            </Box>
                                        </Grid.Col>
                                    )
                                })
                            }
                        </Grid>
                    </Stack>
                </ScrollArea>
            </Stack>
        </Drawer>
    )
}
